/**
 * Component: Current Car
 * ------------------------------------------------------------------------------
 * Responds to EventBus to update the current car.
 *
 * @namespace currentCar
 */
import log from 'salvo-lite/log'

import {updateAttributes} from '@shopify/theme-cart'

import cssClasses from '../helpers/cssClasses'
import {on} from '../helpers/utils'

class CurrentCar {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._settings = {
            currentCarContents: '[js-current-car="contents"]',
            currentCarImage: '[js-current-car="image"]',
            currentCarDetails: '[js-current-car="details"]',
            currentCarButton: '[js-current-car="button"]',
            currentCarClear: '[js-current-car="clear"]',
            currentCarClearing: '[js-current-car="clearing"]',
            currentCarOpen: '[js-current-car-open]',
            emitDataLayer: false,
            ...JSON.parse(elem.getAttribute('data-current-car') || '{}')
        }

        /**
         * DOM node selectors.
         */
        this._nodeSelectors = {
            currentCarContents: [...document.querySelectorAll(this._settings.currentCarContents)],
            currentCarImage: [...document.querySelectorAll(this._settings.currentCarImage)],
            currentCarDetails: [...document.querySelectorAll(this._settings.currentCarDetails)],
            currentCarButton: [...document.querySelectorAll(this._settings.currentCarButton)],
            currentCarClear: [...document.querySelectorAll(this._settings.currentCarClear)],
            currentCarClearing: [...document.querySelectorAll(this._settings.currentCarClearing)],
            currentCarOpen: [...document.querySelectorAll(this._settings.currentCarOpen)]
        }

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise component.
     */
    onInit () {
        this._setListeners()

        if (window.Frame.CurrentCarCookie.isCookieSet()) {
            window.Frame.EventBus.emit('CurrentCar:updated', window.Frame.CurrentCarCookie.get())
        }
    }

    /**
     * Set event listeners.
     */
    _setListeners () {
        window.Frame.EventBus.listen('CurrentCar:setCar', (response) => window.Frame.CurrentCarCookie.set(response))
        window.Frame.EventBus.listen('CurrentCar:updated', (response) => this._updateCurrentCar(response))
        window.Frame.EventBus.listen('CurrentCar:redirect', (response) => window.Frame.CurrentCarCookie.redirect(response))
        window.Frame.EventBus.listen('CurrentCar:clear', () => window.Frame.CurrentCarCookie.clear())
        window.Frame.EventBus.listen('CurrentCar:cleared', () => this._handleCarCleared())

        this._nodeSelectors.currentCarButton.forEach((item) => {
            on('click', item, (event) => this._handleButtonClick(event, item))
        })

        this._nodeSelectors.currentCarOpen.forEach((item) => {
            on('click', item, () => this._handleOpenClick())
        })

        this._nodeSelectors.currentCarClear.forEach((item) => {
            on('click', item, (event) => this._handleClearClick(event))
        })

        window.addEventListener('resize', () => this._updateCarDetails(this._currentCar))
    }

    /**
     * Update the current car globally when the cookie is updated.
     * @param {Object} currentCar - Current car object.
     */
    _updateCurrentCar (currentCar) {
        this._currentCar = currentCar
        this._updateOrderAttributes(currentCar)
        this._updateCarImage(currentCar)
        this._updateCarDetails(currentCar)
        this._setBodyClass(currentCar)
    }

    /**
     * Updates order attributes with current car.
     * @param {Object} currentCar - Current car object.
     */
    _updateOrderAttributes (currentCar) {
        const orderAttribute = this._buildOrderAttribute(currentCar)

        updateAttributes({
            'Car-Details': orderAttribute
        })
    }

    /**
     * Build order attribute value.
     * @param {Object} currentCar - Current car object.
     */
    _buildOrderAttribute (currentCar) {
        if (!currentCar) {
            return window.theme.strings.currentCar.notSet
        }

        const model = this._renameVehicle(currentCar.model)
        let year = currentCar.year

        if (currentCar.years) {
            year = window.theme.strings.currentCar.allYears
        }

        return `Ford ${model} ${year}`
    }

    /**
     * Update the car image globally.
     * @param {Object} currentCar - Current car object.
     */
    _updateCarImage (currentCar) {
        const imageString = this._getImageTemplate(currentCar)

        this._nodeSelectors.currentCarImage.forEach((item) => {
            item.innerHTML = imageString
        })
    }

    /**
     * The image template.
     * @param {Object} currentCar - Current car object.
     * @returns {String} the image tag and URL.
     */
    _getImageTemplate (currentCar) {
        return `
        <img class="current-car__image"
            src="${currentCar.images[200]}"
            srcset="
            ${currentCar.images[200]} 200w,
            ${currentCar.images[400]} 400w,
            ${currentCar.images[750]} 750w,
            ${currentCar.images[1000]} 1000w,
            ${currentCar.images[1500]} 1500w
            "
            sizes="auto"
            alt=""
            onError="Frame.ImageLoadError.init(this)"
        >
        `
    }

    /**
     * Globally update the current car details.
     * @param {Object} currentCar - Current car object.
     */
    _updateCarDetails (currentCar) {
        this._nodeSelectors.currentCarDetails.forEach((item) => {
            item.innerHTML = this._buildCarDetails(currentCar)
        })
    }

    /**
     * Build the car details from the current car object.
     * @param {Object} currentCar - Current car object.
     * @returns {String} returns the details template as a string.
     */
    _buildCarDetails (currentCar) {
        let detailsString = ''
        const validKeys = 'model year colour'
        const objectCount = 2
        let index = 0

        for (const key in currentCar) {
            if (currentCar[key] !== false && validKeys.includes(key)) {
                // If all years is set don't output year value, use 'All years' text string
                if (window.innerWidth <= 768) {
                    if (key === 'year' && currentCar.years) {
                        detailsString += this._formatProperty(key, window.theme.strings.currentCar.allYears)
                    } else {
                        detailsString += this._formatProperty(key, currentCar[key])
                    }
                    if (index < objectCount) detailsString += ' | '
                } else {
                    if (key === 'year' && currentCar.years) {
                        detailsString += this._getDetailsTemplate(key, window.theme.strings.currentCar.allYears)
                    } else {
                        detailsString += this._getDetailsTemplate(key, currentCar[key])
                    }
                }
            }

            index++
        }

        // Add ellipsis to text on mobile when needed
        if (window.innerWidth < 600) {
            let maxLetters = window.innerWidth / 20

            if (window.innerWidth > 425) {
                maxLetters = window.innerWidth / 15
            }

            if (maxLetters < detailsString.length) {
                detailsString = detailsString.slice(0, maxLetters) + '...'
            }
        }

        return detailsString
    }

    /**
     * The details template.
     * @param {String} property the individual model and year values from current car.
     * @returns {String} the details HTML.
     */
    _getDetailsTemplate (key, property) {
        return `
        <div class="car-details__block car-details__block--${key}">
            <span class="car-details__title">${window.theme.strings.currentCar[key]}</span>
            <span class="car-details__text">${this._formatProperty(key, property)}</span>
        </div>
        `
    }

    /**
     * Format the date to words.
     * @param {String} key the object key.
     * @param {String} property the object property.
     */
    _formatProperty (key, property) {
        let formattedProperty = property

        switch (key) {
        case 'model':
            formattedProperty = `Ford ${this._renameVehicle(property)}`
            break

        case 'year':
            formattedProperty = property.replace('-', window.theme.strings.currentCar.yearSeparator)
            break
        }

        return formattedProperty
    }

    /**
     * Renames vehicles.
     * @param {String} model the model name.
     * @return {String} the renamed model.
     */
    _renameVehicle (model) {
        let modelRenamed = model

        switch (model) {
        case 'B-Max':
            modelRenamed = 'B-MAX'
            break
        case 'C-Max':
            modelRenamed = 'C-MAX'
            break
        case 'S-Max':
            modelRenamed = 'S-MAX'
            break
        case 'Fiesta-Van':
            modelRenamed = 'Fiesta Van'
            break
        case 'Fiesta-ST':
            modelRenamed = 'Fiesta ST'
            break
        case 'Focus-ST':
            modelRenamed = 'Focus ST'
            break
        case 'Puma-ST':
            modelRenamed = 'Puma ST'
            break
        case 'Focus-RS':
            modelRenamed = 'Focus RS'
            break
        case 'Tourneo-Connect':
            modelRenamed = 'Tourneo Connect'
            break
        case 'Tourneo-Courier':
            modelRenamed = 'Tourneo Courier'
            break
        case 'Tourneo-Custom':
            modelRenamed = 'Tourneo Custom'
            break
        case 'Transit-Courier':
            modelRenamed = 'Transit Courier'
            break
        case 'Transit-Connect':
            modelRenamed = 'Transit Connect'
            break
        case 'Transit-Custom':
            modelRenamed = 'Transit Custom'
            break
        case 'Mustang-Mach-E':
            modelRenamed = 'Mustang Mach-E'
            break
        }

        return modelRenamed
    }

    /**
     * Add class to document to hide/show current car elements.
     * @param {Object} currentCar used to determine classes.
     */
    _setBodyClass (currentCar) {
        document.body.classList.add(cssClasses.currentCarSet)
        document.body.classList.remove(cssClasses.settingCurrentCar)
        window.Frame.EventBus.emit('SiteHeader:update')

        if ([currentCar.emissionCode, currentCar.engineCode, currentCar.vehicleCode, currentCar.vinNumber].every(Boolean)) {
            document.body.classList.add(cssClasses.currentCarVinSet)
        }
    }

    /**
     * Handles the action buttons click event.
     * @param {Event} event the click event.
     * @param {Object} element the clicked element.
     */
    _handleButtonClick (event, element) {
        event.preventDefault()
        const type = element.getAttribute('data-button-action')
        window.Frame.EventBus.emit('CurrentCar:redirect', type)
    }

    /**
     * Handle open click, opens current car banner.
     */
    _handleOpenClick () {
        document.body.classList.add(cssClasses.currentCarView)
    }

    /**
     * Handles the clear button click.
     */
    _handleClearClick (event) {
        event.stopPropagation()
        this._nodeSelectors.currentCarClearing.forEach((item) => {
            item.classList.add(cssClasses.active)
        })

        window.Frame.EventBus.emit('CurrentCar:clear')
    }

    /**
     * Handle current car cleared event.
     */
    _handleCarCleared () {
        this._clearCurrentCar()
        this._updateOrderAttributes(false)
    }

    /**
     * Clears the current car status after the cookie has been deleted.
     */
    _clearCurrentCar () {
        window.Frame.EventBus.emit('Overlay:vinTarget:hide', 'other')

        document.body.classList.remove(cssClasses.currentCarSet)
        document.body.classList.remove(cssClasses.currentCarVinSet)
        document.body.classList.remove(cssClasses.settingCurrentCar)

        this._nodeSelectors.currentCarClearing.forEach((item) => {
            item.classList.remove(cssClasses.active)
        })
    }

    /**
     * Expose public interface.
     */
    // return Object.freeze({
    //     init,
    // });
}

export default CurrentCar



// WEBPACK FOOTER //
// ./src/resources/scripts/components/CurrentCar.js